<template>
  <v-app>
    <v-app-bar app>
      <v-toolbar-title>{{ t('admConfig.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-btn icon @click="logout">
        <v-icon>mdi-logout</v-icon>
      </v-btn>
    </v-app-bar>
    
    <v-main>
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <v-icon left>mdi-cog</v-icon>
                {{ t('admConfig.configuration') }}
              </v-card-title>
              <v-card-text>
                <p>{{ t('admConfig.description') }}</p>
                
                <!-- Qui aggiungeremo i controlli di configurazione -->
                <v-row>
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title>{{ t('admConfig.generalSettings') }}</v-card-title>
                      <v-card-text>
                        <!-- Placeholder per impostazioni generali -->
                        <v-text-field
                          :label="t('admConfig.localPath')"
                          v-model="config.localPath"
                          outlined
                          :placeholder="t('admConfig.localPathPlaceholder')"
                          :hint="t('admConfig.localPathHint')"
                          persistent-hint
                          :error-messages="getPathError('localPath')"
                        />
                        <v-text-field
                          :label="t('admConfig.templatePath')"
                          v-model="config.templatePath"
                          outlined
                          :placeholder="t('admConfig.templatePathPlaceholder')"
                          :hint="t('admConfig.templatePathHint')"
                          persistent-hint
                          class="mt-3"
                          :error-messages="getPathError('templatePath')"
                        />
                        <v-text-field
                          :label="t('admConfig.archivePath')"
                          v-model="config.archivePath"
                          outlined
                          :placeholder="t('admConfig.archivePathPlaceholder')"
                          :hint="t('admConfig.archivePathHint')"
                          persistent-hint
                          class="mt-3"
                          :error-messages="getPathError('archivePath')"
                        />
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                
                <v-row class="mt-4">
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title>{{ t('admConfig.systemInfo') }}</v-card-title>
                      <v-card-text>
                        <v-simple-table>
                          <tbody>
                            <tr>
                              <td><strong>{{ t('admConfig.status') }}</strong></td>
                              <td>
                                <v-chip 
                                  :color="systemStatus.color" 
                                  small
                                >
                                  {{ t(`admConfig.${systemStatus.status}`) }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr>
                              <td><strong>{{ t('admConfig.lastUpdate') }}</strong></td>
                              <td>{{ systemStatus.lastUpdate }}</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
              
              <v-card-actions>
                <v-spacer />
                <v-btn 
                  color="primary" 
                  @click="saveConfiguration"
                  :loading="saving"
                  :disabled="!isConfigValid"
                >
                  {{ t('admConfig.save') }}
                </v-btn>
                <v-btn 
                  color="secondary" 
                  @click="resetConfiguration"
                >
                  {{ t('admConfig.reset') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { API_CONFIG, apiCall } from '../utils/api.js'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const localBackendStore = useLocalBackendStore()

// Stato reattivo per la configurazione
const config = reactive({
  localPath: '',
  templatePath: '',
  archivePath: ''
})

// Stato del sistema
const systemStatus = reactive({
  status: 'online',
  color: 'success',
  lastUpdate: new Date().toLocaleString()
})

const saving = ref(false)

// Verifica se i percorsi sono duplicati
const isConfigValid = computed(() => {
  const paths = [
    config.localPath?.trim(),
    config.templatePath?.trim(),
    config.archivePath?.trim()
  ].filter(p => p && p.length > 0)
  
  // Se non ci sono percorsi configurati, è valido
  if (paths.length === 0) return true
  
  // Verifica che non ci siano duplicati
  const uniquePaths = new Set(paths.map(p => p.toLowerCase()))
  return uniquePaths.size === paths.length
})

// Funzione per ottenere messaggi di errore per ciascun campo
const getPathError = (fieldName) => {
  const currentPath = config[fieldName]?.trim()
  if (!currentPath || currentPath.length === 0) return []
  
  const otherPaths = Object.entries(config)
    .filter(([key, value]) => key !== fieldName && value?.trim() && value.trim().length > 0)
    .map(([key, value]) => ({ key, value: value.trim().toLowerCase() }))
  
  const duplicate = otherPaths.find(p => p.value === currentPath.toLowerCase())
  
  if (duplicate) {
    const pathNames = {
      localPath: t('admConfig.localPath'),
      templatePath: t('admConfig.templatePath'),
      archivePath: t('admConfig.archivePath')
    }
    return [t('admConfig.duplicatePathError', { path: pathNames[duplicate.key] })]
  }
  
  return []
}

// Metodi
const goBack = () => {
  router.push('/home')
}

const logout = async () => {
  authStore.logout()
  router.push('/login')
}

const saveConfiguration = async () => {
  if (!isConfigValid.value) {
    console.error('Configurazione non valida: percorsi duplicati')
    return
  }
  
  saving.value = true
  try {
    console.log('Invio configurazione:', {
      localPath: config.localPath,
      templatePath: config.templatePath,
      archivePath: config.archivePath
    })
    
    // Salva i percorsi nel LocalBackend
    const result = await apiCall('/api/config/config', {
      method: 'PUT',
      body: JSON.stringify({
        localPath: config.localPath,
        templatePath: config.templatePath,
        archivePath: config.archivePath
      }),
      backend: 'local',
      includeAuth: false
    })
    
    console.log('Risposta dal server:', result)
    console.log('Configurazione salvata con successo')
    
    // Riverifica la configurazione dopo il salvataggio
    await localBackendStore.checkConnection()
  } catch (error) {
    console.error('Errore nel salvataggio:', error)
  } finally {
    saving.value = false
  }
}

const resetConfiguration = () => {
  config.localPath = ''
  config.templatePath = ''
  config.archivePath = ''
}

const loadConfiguration = async () => {
  try {
    console.log('Caricamento configurazione dal LocalBackend...')
    
    // Carica i percorsi dal LocalBackend
    const pathsConfig = await apiCall('/api/config/config', {
      method: 'GET',
      backend: 'local',
      includeAuth: false
    })
    
    console.log('Configurazione ricevuta:', pathsConfig)
    
    if (pathsConfig) {
      config.localPath = pathsConfig.localPath || ''
      config.templatePath = pathsConfig.templatePath || ''
      config.archivePath = pathsConfig.archivePath || ''
      
      console.log('Configurazione applicata:', config)
    }
  } catch (error) {
    console.error('Errore nel caricamento:', error)
  }
}

onMounted(() => {
  loadConfiguration()
})
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}
</style>